import { overrideTheme } from '@aspendental/themes';
import { createTheme, GlobalStyles, GlobalStylesProps, ThemeProvider, ScopedCssBaseline } from '@mui/material';
import { match } from 'css-mediaquery';
import dynamic from 'next/dynamic';
import { TAGBrand } from '../types';
import { lovet as lovetFeatures } from './tagfeatures';
import LazyHydrate from '@shared-components/components/LazyHydrate/LazyHydrate';
import { useAppContext } from '@shared-components/context';
import { PageTheme } from '@shared-components/context/PageThemeProvider.ctx';
import { applyFluidTypography } from '@utils/applyFluidTypography';
import { resolveBrandThemeConfig } from '@utils/resolveBrandThemeConfig';
const FontPreloader = dynamic(() => import(/* webpackChunkName:"FontPreloader" */'./FontPreloader').then(mod => mod.FontPreloader));
declare module '@mui/material/styles' {
  interface Theme {
    brand: string;
    tagFeatures: {
      PAGE_THEME_ENABLED: boolean;
    };
  }
  interface ThemeOptions {
    brand: string;
    tagFeatures?: {
      PAGE_THEME_ENABLED?: boolean;
    };
  }
}
const globalStyles: GlobalStylesProps['styles'] = {
  html: {
    scrollBehavior: 'smooth'
  },
  'html, body': {
    width: '100%',
    margin: 0
  },
  body: {
    overflowX: 'hidden'
  },
  '@media screen and (prefers-reduced-motion: reduce)': {
    html: {
      scrollBehavior: 'auto'
    }
  }
};
export interface TAGThemeProviderProps {
  branding: TAGBrand & string;
  pageTheme?: PageTheme;
  children?: React.ReactNode;
  deviceType?: 'desktop' | 'mobile';
}
export default function TAGThemeProvider({
  branding = TAGBrand.Lovet,
  pageTheme: _pageTheme,
  children,
  deviceType = 'mobile'
}: Readonly<TAGThemeProviderProps>) {
  const brandFeatures = lovetFeatures;
  const brandTheme = resolveBrandThemeConfig(branding);
  const {
    config
  } = useAppContext();
  const enableFluidTypography = !!config.featureFlags?.enableFluidTypography;
  const updatedMuiTypography = enableFluidTypography ? applyFluidTypography(brandTheme, branding ?? null).components?.MuiTypography : brandTheme.components?.MuiTypography;
  const theme = createTheme(overrideTheme(brandTheme, {
    brand: branding,
    tagFeatures: {
      ...brandFeatures
    },
    components: {
      // https://mui.com/material-ui/react-use-media-query/#server-side-rendering
      MuiUseMediaQuery: {
        defaultProps: {
          ssrMatchMedia: query => ({
            matches: match(query, {
              width: deviceType === 'mobile' ? '0px' : '901px'
            })
          })
        }
      },
      MuiTypography: {
        ...brandTheme.components?.MuiTypography,
        ...updatedMuiTypography
      }
    },
    breakpoints: {
      ...brandTheme.breakpoints
    }
  }));
  return <ThemeProvider theme={theme} data-sentry-element="ThemeProvider" data-sentry-component="TAGThemeProvider" data-sentry-source-file="TAGThemeProvider.tsx">
			<LazyHydrate ssrOnly data-sentry-element="LazyHydrate" data-sentry-source-file="TAGThemeProvider.tsx">
				<FontPreloader brand={branding} data-sentry-element="FontPreloader" data-sentry-source-file="TAGThemeProvider.tsx" />
			</LazyHydrate>
			<ScopedCssBaseline id="mui-scoped-baseline" data-sentry-element="ScopedCssBaseline" data-sentry-source-file="TAGThemeProvider.tsx">
				<>
					<GlobalStyles styles={globalStyles} data-sentry-element="GlobalStyles" data-sentry-source-file="TAGThemeProvider.tsx" />
					{children}
				</>
			</ScopedCssBaseline>
		</ThemeProvider>;
}